import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import './ArticulosSeguridad.css';

export default function ArticulosSeguridad({ usuario, onBack = null }) {
    const [articulos, setArticulos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        categoria: '',
        requiere_talla: false
    });
    const [editingId, setEditingId] = useState(null);
    const [categorias, setCategorias] = useState([]);
    const [tallas, setTallas] = useState([]);
    const [nuevaTalla, setNuevaTalla] = useState('');
    const [archivosArticulo, setArchivosArticulo] = useState(null);
    const [archivos, setArchivos] = useState([]);
    const [uploadingArchivo, setUploadingArchivo] = useState('');

    const CATEGORIAS_ARCHIVO = [
        { key: 'ficha_tecnica', label: 'Ficha técnica', icon: '📄', countField: 'fichas_count' },
        { key: 'especificacion', label: 'Especificaciones', icon: '📑', countField: 'especificaciones_count' },
        { key: 'certificacion', label: 'Certificaciones', icon: '🏅', countField: 'certificaciones_count' }
    ];

    const empresaId = usuario.empresa_id;

    useEffect(() => {
        if (empresaId) {
            fetchArticulos();
        }
    }, [empresaId]);

    useEffect(() => {
        fetchCategorias();
    }, []);

    const fetchWithAuth = async (url, options = {}) => {
        const token = localStorage.getItem('authToken');
        return fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`
            }
        });
    };

    const fetchArticulos = async () => {
        try {
            setLoading(true);
            const response = await fetchWithAuth(
                `${API_BASE_URL}/api/articulos-seguridad?id_empresa=${empresaId}`
            );

            if (response.ok) {
                const data = await response.json();
                setArticulos(data);
            } else {
                setError('Error al cargar artículos');
            }
        } catch (err) {
            console.error('Error al cargar artículos:', err);
            setError('Error al cargar artículos');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setError('');
        setSuccess('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.nombre.trim()) {
            setError('El nombre del artículo es obligatorio');
            return;
        }

        try {
            setLoading(true);
            setError('');
            setSuccess('');

            const isEditing = Boolean(editingId);
            const endpoint = isEditing
                ? `${API_BASE_URL}/api/articulos-seguridad/${editingId}`
                : `${API_BASE_URL}/api/articulos-seguridad`;
            const method = isEditing ? 'PUT' : 'POST';

            const response = await fetchWithAuth(endpoint, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    id_empresa: empresaId,
                    tallas: formData.requiere_talla ? tallas : []
                })
            });

            if (response.ok) {
                setSuccess(isEditing ? 'Artículo actualizado exitosamente' : 'Artículo guardado exitosamente');
                setFormData({
                    nombre: '',
                    descripcion: '',
                    categoria: '',
                    requiere_talla: false
                });
                setTallas([]);
                setNuevaTalla('');
                setShowForm(false);
                setEditingId(null);
                await fetchArticulos();
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Error al guardar artículo');
            }
        } catch (err) {
            console.error('Error al guardar artículo:', err);
            setError('Error al guardar artículo');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (articulo) => {
        setFormData({
            nombre: articulo.nombre,
            descripcion: articulo.descripcion || '',
            categoria: articulo.categoria || '',
            requiere_talla: articulo.requiere_talla || false
        });
        setTallas((articulo.tallas || []).map(t => t.talla));
        setNuevaTalla('');
        setEditingId(articulo.id);
        setShowForm(true);
    };

    const handleAddTalla = () => {
        const val = nuevaTalla.trim();
        if (val && !tallas.includes(val)) {
            setTallas([...tallas, val]);
            setNuevaTalla('');
        }
    };

    const handleRemoveTalla = (talla) => {
        setTallas(tallas.filter(t => t !== talla));
    };

    const handleTallaKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTalla();
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingId(null);
        setFormData({
            nombre: '',
            descripcion: '',
            categoria: '',
            requiere_talla: false
        });
        setTallas([]);
        setNuevaTalla('');
        setError('');
    };

    const openArchivos = async (articulo) => {
        setArchivosArticulo(articulo);
        setArchivos([]);
        try {
            const res = await fetchWithAuth(`${API_BASE_URL}/api/articulos-seguridad/${articulo.id}/archivos`);
            if (res.ok) {
                setArchivos(await res.json());
            }
        } catch (err) {
            console.error('Error al cargar archivos:', err);
        }
    };

    const uploadArchivos = async (categoria, files) => {
        if (!files || !files.length || !archivosArticulo) return;
        const data = new FormData();
        data.append('categoria', categoria);
        Array.from(files).forEach(f => data.append('archivo', f));
        setUploadingArchivo(categoria);
        setError('');
        try {
            const res = await fetchWithAuth(
                `${API_BASE_URL}/api/articulos-seguridad/${archivosArticulo.id}/archivos`,
                { method: 'POST', body: data }
            );
            if (res.ok) {
                await openArchivos(archivosArticulo);
                await fetchArticulos();
            } else {
                const err = await res.json();
                setError(err.error || 'Error al subir archivo');
            }
        } catch (err) {
            console.error('Error al subir archivo:', err);
            setError('Error al subir archivo');
        } finally {
            setUploadingArchivo('');
        }
    };

    const deleteArchivo = async (id) => {
        if (!window.confirm('¿Eliminar este archivo?')) return;
        try {
            const res = await fetchWithAuth(
                `${API_BASE_URL}/api/articulos-seguridad-archivos/${id}`,
                { method: 'DELETE' }
            );
            if (res.ok) {
                await openArchivos(archivosArticulo);
                await fetchArticulos();
            } else {
                setError('Error al eliminar el archivo');
            }
        } catch (err) {
            console.error('Error al eliminar archivo:', err);
            setError('Error al eliminar el archivo');
        }
    };

    const filteredArticulos = articulos.filter(a =>
        a.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (a.categoria && a.categoria.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const fetchCategorias = async () => {
        try {
            const response = await fetchWithAuth(
                `${API_BASE_URL}/api/categorias-seguridad`
            );

            if (response.ok) {
                const data = await response.json();
                setCategorias(data);
            } else {
                console.error('Error al cargar categorías');
            }
        } catch (err) {
            console.error('Error al cargar categorías:', err);
        }
    };

    if (loading && articulos.length === 0) {
        return (
            <div className="container">
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p>Cargando artículos...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container articulos-container">
            <div className="header-section articulos-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {onBack && (
                        <button onClick={onBack} className="btn-back">
                            ← Atrás
                        </button>
                    )}
                    <div>
                        <h1>🛡️ Artículos de Seguridad</h1>
                        <p className="header-subtitle">Gestiona el catálogo de artículos de seguridad y EPP</p>
                    </div>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="search-section articulos-search">
                <div className="search-group">
                    <label className="search-label">Buscar Artículos</label>
                    <div className="search-wrapper">
                        <span className="search-icon">🔍</span>
                        <input
                            type="text"
                            placeholder="Ej: Casco de seguridad"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                    </div>
                </div>
                <button
                    className="btn-primary btn-nuevo-articulo"
                    onClick={() => {
                        setFormData({
                            nombre: '',
                            descripcion: '',
                            categoria: '',
                            requiere_talla: false
                        });
                        setTallas([]);
                        setNuevaTalla('');
                        setEditingId(null);
                        setShowForm(true);
                    }}
                >
                    <span>➕</span> Nuevo Artículo
                </button>
            </div>

            {showForm && (
                <div className="modal-overlay" onClick={handleCancel} style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '8px',
                        width: '90%',
                        maxWidth: '500px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        position: 'relative'
                    }}>
                        <div className="modal-header" style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '20px',
                            borderBottom: '1px solid #eee',
                            paddingBottom: '15px'
                        }}>
                            <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#333' }}>{editingId ? 'Editar Artículo' : 'Nuevo Artículo'}</h2>
                            <button className="close-btn" onClick={handleCancel} style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '24px',
                                cursor: 'pointer',
                                color: '#666'
                            }}>×</button>
                        </div>

                        <form onSubmit={handleSubmit} className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontWeight: '600', color: '#333' }}>Nombre del Artículo *</label>
                                <input
                                    type="text"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleInputChange}
                                    placeholder="Ej: Casco de seguridad"
                                    required
                                    style={{
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        width: '100%',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontWeight: '600', color: '#333' }}>Descripción</label>
                                <textarea
                                    name="descripcion"
                                    value={formData.descripcion}
                                    onChange={handleInputChange}
                                    placeholder="Descripción del artículo"
                                    rows="3"
                                    style={{
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        width: '100%',
                                        boxSizing: 'border-box',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>

                            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontWeight: '600', color: '#333' }}>Categoría</label>
                                    <select
                                        name="categoria"
                                        value={formData.categoria}
                                        onChange={handleInputChange}
                                        style={{
                                            padding: '10px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            width: '100%',
                                            boxSizing: 'border-box',
                                            backgroundColor: 'white'
                                        }}
                                    >
                                        <option value="">Seleccionar categoría</option>
                                        {categorias.map(cat => (
                                            <option key={cat.id} value={cat.nombre}>{cat.nombre}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group checkbox-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '10px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '500' }}>
                                        <input
                                            type="checkbox"
                                            name="requiere_talla"
                                            checked={formData.requiere_talla}
                                            onChange={handleInputChange}
                                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                        />
                                        Requiere talla
                                    </label>
                                </div>
                            </div>

                            {formData.requiere_talla && (
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontWeight: '600', color: '#333' }}>Tallas disponibles</label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <input
                                            type="text"
                                            value={nuevaTalla}
                                            onChange={(e) => setNuevaTalla(e.target.value)}
                                            onKeyDown={handleTallaKeyDown}
                                            placeholder="Ej: S, M, L, XL..."
                                            maxLength="10"
                                            style={{
                                                flex: 1,
                                                padding: '10px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                fontSize: '14px',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddTalla}
                                            style={{
                                                padding: '10px 16px',
                                                backgroundColor: '#4f46e5',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontWeight: '500'
                                            }}
                                        >
                                            + Agregar
                                        </button>
                                    </div>
                                    {tallas.length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {tallas.map((talla, idx) => (
                                                <span
                                                    key={idx}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        padding: '4px 10px',
                                                        backgroundColor: '#e0e7ff',
                                                        color: '#4f46e5',
                                                        borderRadius: '12px',
                                                        fontSize: '0.85rem',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {talla}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveTalla(talla)}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            color: '#4f46e5',
                                                            cursor: 'pointer',
                                                            padding: 0,
                                                            fontSize: '1rem',
                                                            lineHeight: 1
                                                        }}
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <small style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                                        Presiona Enter o clic en Agregar para añadir una talla.
                                    </small>
                                </div>
                            )}

                            <div className="modal-footer" style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '10px',
                                marginTop: '15px',
                                paddingTop: '15px',
                                borderTop: '1px solid #eee'
                            }}>
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    style={{
                                        padding: '10px 16px',
                                        backgroundColor: '#f1f5f9',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontWeight: '500'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    style={{
                                        padding: '10px 16px',
                                        backgroundColor: '#3b82f6',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontWeight: '500',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    {loading ? 'Guardando...' : '✓ Guardar Artículo'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {filteredArticulos.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">📦</div>
                    <h3>No hay artículos registrados</h3>
                    <p>Comienza creando tu primer artículo de seguridad</p>
                    <button
                        className="btn-crear-primer-articulo"
                        onClick={() => setShowForm(true)}
                    >
                        <span className="btn-icon">➕</span>
                        <span className="btn-text">Crear Primer Artículo</span>
                    </button>
                </div>
            ) : (
                <div className="articulos-grid">
                    {filteredArticulos.map(articulo => (
                        <div key={articulo.id} className="articulo-card">
                            <div className="card-header">
                                <div className="card-icon">
                                    {articulo.categoria === 'Ropa' && '👕'}
                                    {articulo.categoria === 'Casco' && '🪖'}
                                    {articulo.categoria === 'Guantes' && '🧤'}
                                    {articulo.categoria === 'Calzado' && '👞'}
                                    {articulo.categoria === 'Otros' && '📦'}
                                    {!articulo.categoria && '🛡️'}
                                </div>
                                <div className="card-title">
                                    <h3>{articulo.nombre}</h3>
                                    {articulo.categoria && (
                                        <span className="categoria-badge">{articulo.categoria}</span>
                                    )}
                                    <div className="doc-indicators">
                                        {CATEGORIAS_ARCHIVO.map(cat => (
                                            <span
                                                key={cat.key}
                                                className={`doc-indicator ${(articulo[cat.countField] || 0) > 0 ? 'on' : 'off'}`}
                                                title={`${cat.label}: ${(articulo[cat.countField] || 0) > 0 ? `${articulo[cat.countField]} archivo(s)` : 'sin archivos'}`}
                                            >
                                                {cat.icon}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="card-body">
                                {articulo.descripcion && (
                                    <p className="descripcion">{articulo.descripcion}</p>
                                )}
                                <div className="card-meta">
                                    <div className="meta-item">
                                        <span className="meta-label">Requiere Talla:</span>
                                        {articulo.requiere_talla ? (
                                            <span className="badge-yes">✓ Sí</span>
                                        ) : (
                                            <span className="badge-no">✗ No</span>
                                        )}
                                    </div>
                                    {articulo.requiere_talla && articulo.tallas && articulo.tallas.length > 0 && (
                                        <div className="meta-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                            <span className="meta-label">Tallas:</span>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                {articulo.tallas.map(t => (
                                                    <span
                                                        key={t.id}
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            padding: '2px 8px',
                                                            backgroundColor: '#e0e7ff',
                                                            color: '#4f46e5',
                                                            borderRadius: '10px',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 500
                                                        }}
                                                    >
                                                        {t.talla}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="card-footer" style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    className="btn-edit-card"
                                    onClick={() => handleEdit(articulo)}
                                    title="Editar artículo"
                                >
                                    ✏️ Editar
                                </button>
                                <button
                                    className="btn-edit-card btn-archivos-card"
                                    onClick={() => openArchivos(articulo)}
                                    title="Fichas técnicas, especificaciones y certificaciones"
                                >
                                    📎 Archivos
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* MODAL DE ARCHIVOS DEL ARTÍCULO */}
            {archivosArticulo && (
                <div className="modal-overlay" onClick={() => setArchivosArticulo(null)} style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '8px',
                        width: '90%',
                        maxWidth: '600px',
                        maxHeight: '85vh',
                        overflowY: 'auto',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        position: 'relative'
                    }}>
                        <div className="modal-header" style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '20px',
                            borderBottom: '1px solid #eee',
                            paddingBottom: '15px'
                        }}>
                            <h2 style={{ margin: 0, fontSize: '1.4rem', color: '#333' }}>
                                📎 Archivos — {archivosArticulo.nombre}
                            </h2>
                            <button className="close-btn" onClick={() => setArchivosArticulo(null)} style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '24px',
                                cursor: 'pointer',
                                color: '#666'
                            }}>×</button>
                        </div>

                        {CATEGORIAS_ARCHIVO.map(cat => {
                            const files = archivos.filter(a => a.categoria === cat.key);
                            return (
                                <div key={cat.key} className="archivo-section" style={{
                                    marginBottom: '20px',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    padding: '15px'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '10px'
                                    }}>
                                        <h4 style={{ margin: 0, color: '#333', fontSize: '1rem' }}>
                                            {cat.icon} {cat.label}
                                        </h4>
                                        <label style={{
                                            padding: '6px 12px',
                                            backgroundColor: '#4f46e5',
                                            color: 'white',
                                            borderRadius: '6px',
                                            cursor: uploadingArchivo ? 'not-allowed' : 'pointer',
                                            fontSize: '0.85rem',
                                            fontWeight: '500',
                                            opacity: uploadingArchivo && uploadingArchivo !== cat.key ? 0.6 : 1
                                        }}>
                                            {uploadingArchivo === cat.key ? 'Subiendo...' : '⬆️ Subir PDF'}
                                            <input
                                                type="file"
                                                accept="application/pdf,.pdf"
                                                multiple
                                                hidden
                                                disabled={Boolean(uploadingArchivo)}
                                                onChange={(e) => {
                                                    uploadArchivos(cat.key, e.target.files);
                                                    e.target.value = '';
                                                }}
                                            />
                                        </label>
                                    </div>
                                    {files.length > 0 ? (
                                        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                                            {files.map(f => (
                                                <li key={f.id} style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '8px 10px',
                                                    borderBottom: '1px solid #f1f5f9',
                                                    gap: '10px'
                                                }}>
                                                    <a
                                                        href={`${API_BASE_URL}/uploads/${f.nombre_archivo}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            color: '#4f46e5',
                                                            textDecoration: 'none',
                                                            fontSize: '0.9rem',
                                                            flex: 1,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                        title={f.nombre_original}
                                                    >
                                                        📄 {f.nombre_original}
                                                    </a>
                                                    <span style={{ color: '#94a3b8', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                                        {new Date(f.fecha_creacion).toLocaleDateString('es-CL')}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => deleteArchivo(f.id)}
                                                        title="Eliminar archivo"
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            fontSize: '1rem',
                                                            padding: '2px 4px'
                                                        }}
                                                    >
                                                        🗑️
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0 }}>
                                            Sin archivos en esta categoría.
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

        </div>
    );
}
